import { useState, useEffect } from 'react';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import './App.css';
import FormFunction from './FormFunction';
import SignIn from './SignIn';
import SetupWizard from './SetupWizard';
import ReceivingPage from './ReceivingPage';
import StatsPage from './StatsPage';
import ErrorLogPage from './ErrorLogPage';
import UnfinishedOrdersPage from './UnfinishedOrdersPage';
import Modal from 'react-modal';
import AdminPanel from './AdminPanel';
import PlatformAdminPage from './PlatformAdminPage';
import UpdateBanner from './UpdateBanner';
const API_BASE_URL = process.env.REACT_APP_API_URL;

// The backend sleeps when idle, so the first request after a quiet spell can be
// slow or fail while it wakes up. Retry a few times before giving up.
const STARTUP_ATTEMPTS = 4;
const STARTUP_TIMEOUT_MS = 20000;

// A timeout or an error status throws, so it gets retried instead of hanging on
// "Starting up…" forever or being read as "this org isn't set up".
async function fetchStartupJson(path, slug) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), STARTUP_TIMEOUT_MS);
  try {
    const res = await fetch(`${API_BASE_URL}${path}`, { headers: { 'X-Org-Slug': slug }, signal: controller.signal });
    if (!res.ok) throw new Error(`${path} returned ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

// Dummy MSAL instance used only for Google orgs (MsalProvider still required in tree)
let _dummyMsal = null;
function getDummyMsal() {
  if (!_dummyMsal) {
    _dummyMsal = new PublicClientApplication({
      auth: { clientId: '00000000-0000-0000-0000-000000000000' },
    });
  }
  return _dummyMsal;
}

// ── Feature cards shown on the landing screen ──────────────────────────────
const FEATURES = [
  {
    icon: (
      <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2
             M9 5a2 2 0 002 2h2a2 2 0 002-2
             M9 5a2 2 0 012-2h2a2 2 0 012 2
             m-6 9l2 2 4-4" />
      </svg>
    ),
    label: 'Order Verification',
    desc: 'Scan & verify packing lists against sales orders',
  },
  {
    icon: (
      <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4
             m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
    label: 'PO Receiving',
    desc: 'Scan items to match and receive purchase orders',
  },
];

// ── Nav tab definitions (visibility gated per role in render) ───────────────
const NAV_TABS = [
  { id: 'home',      label: 'Order Verification', roles: ['Admin','Supervisor','Verifier','User'] },
  { id: 'receiving', label: 'PO Receiving',        roles: null }, // null = use poReceivingAccess
  { id: 'stats',     label: 'Stats',               roles: ['Admin','Supervisor'] },
  { id: 'unfinished-orders', label: 'Unfinished Orders', roles: ['Admin','Supervisor','Verifier','User'] },
  { id: 'error-log', label: 'Logs',                roles: null }, // null = use isPlatformAdmin, see filter below
  { id: 'platform-admin', label: 'Super Admin',    roles: null }, // null = use isPlatformAdmin, see filter below
];

export default function App() {
  const [startupState, setStartupState] = useState('loading');
  const [startupAttempt, setStartupAttempt] = useState(1);
  const [msalInstance, setMsalInstance]   = useState(null);
  const [currentPage, setCurrentPage]     = useState('home');
  const [showSettings, setShowSettings]   = useState(false);
  const [user, setUser]                   = useState(null);
  const [pendingOrderID, setPendingOrderID] = useState(null);

  // Lets Stats' "In-Progress Orders" list jump straight into that order's draft
  // instead of making Olesia retype the order number on the home screen.
  const openSavedOrder = (orderID) => {
    setPendingOrderID(orderID);
    setCurrentPage('home');
  };

  useEffect(() => { checkStartup(); }, []);

const handleLogin = (userInfo) => {
  setUser(userInfo);
  setCurrentPage('home');
  setShowSettings(false);
};
  const handleLogout = () => {
    setUser(null);
    setCurrentPage('home');
    localStorage.removeItem('authProvider');
    localStorage.removeItem('authToken');
  };

  async function checkStartup() {
    const params     = new URLSearchParams(window.location.search);
    const urlSlug    = params.get('org');
    const storedSlug = localStorage.getItem('orgSlug');
    const slug       = urlSlug || storedSlug;

    if (!slug) { setStartupState('no-org'); return; }
    localStorage.setItem('orgSlug', slug);

    setStartupState('loading');
    for (let attempt = 1; attempt <= STARTUP_ATTEMPTS; attempt++) {
      setStartupAttempt(attempt);
      try {
        await loadOrgConfig(slug);
        return;
      } catch (err) {
        console.error(`Startup check failed (attempt ${attempt} of ${STARTUP_ATTEMPTS}):`, err);
        if (attempt < STARTUP_ATTEMPTS) await new Promise(r => setTimeout(r, 2000 * attempt));
      }
    }
    setStartupState('unreachable');
  }

  async function loadOrgConfig(slug) {
    const { configured } = await fetchStartupJson('/api/setup/status', slug);
    if (!configured) { setStartupState('needs-setup'); return; }

    const { authProvider } = await fetchStartupJson('/api/config/auth-provider', slug);
    localStorage.setItem('authProvider', authProvider);

    if (authProvider === 'google') {
      setMsalInstance(null);
      setStartupState('ready');
      return;
    }

    const { clientId, tenantId } = await fetchStartupJson('/api/config/azure', slug);

    const instance = new PublicClientApplication({
      auth: { clientId, authority: `https://login.microsoftonline.com/${tenantId}`, redirectUri: window.location.origin },
      cache: { cacheLocation: 'sessionStorage' },
    });
    await instance.initialize();
    setMsalInstance(instance);
    setStartupState('ready');
  }

  // ── Render: loading ─────────────────────────────────────────────────────
  if (startupState === 'loading') {
    return (
      <div className="App">
        <header className="App-header">
          <div className="landing-center">
            <div className="landing-card" style={{ textAlign: 'center' }}>
              <img src="/2-check-logo-colorful.png" className="landing-logo" alt="logo" style={{ marginBottom: '1rem' }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                {startupAttempt > 1 ? `Waking up 2Check… (try ${startupAttempt} of ${STARTUP_ATTEMPTS})` : 'Starting up…'}
              </div>
            </div>
          </div>
        </header>
      </div>
    );
  }

  // ── Render: backend unreachable after every retry ────────────────────────
  if (startupState === 'unreachable') {
    return (
      <div className="App">
        <header className="App-header">
          <div className="landing-center">
            <div className="landing-card" style={{ textAlign: 'center' }}>
              <img src="/2-check-logo-colorful.png" className="landing-logo" alt="logo" style={{ marginBottom: '1.25rem' }} />
              <h1 className="barcode-title">Can't reach 2Check</h1>
              <p className="landing-subtitle" style={{ marginTop: '0.5rem', marginBottom: '1.25rem' }}>
                The server didn't answer. Check the wifi, then try again.
              </p>
              <button type="button" className="btn-primary-action" onClick={checkStartup}>Try again</button>
            </div>
          </div>
        </header>
      </div>
    );
  }

  // ── Render: no org ───────────────────────────────────────────────────────
  if (startupState === 'no-org') {
    return (
      <div className="App">
        <header className="App-header">
          <div className="landing-center">
            <div className="landing-card" style={{ textAlign: 'center' }}>
              <img src="/2-check-logo-colorful.png" className="landing-logo" alt="logo" style={{ marginBottom: '1.25rem' }} />
              <h1 className="barcode-title">Packing List Verification</h1>
              <p className="landing-subtitle" style={{ marginTop: '0.5rem' }}>
                Please use the link provided by your administrator to access your organization.
              </p>
            </div>
          </div>
        </header>
      </div>
    );
  }

  // ── Render: needs setup ──────────────────────────────────────────────────
  if (startupState === 'needs-setup') {
    return <SetupWizard onComplete={() => window.location.reload()} />;
  }

  // ── Render: ready ────────────────────────────────────────────────────────
const visibleTabs = NAV_TABS.filter(t => {
  if (!user) return false;
  if (t.id === 'receiving') return user.poReceivingAccess || user.role === 'Admin';
  if (t.id === 'platform-admin') return !!user.isPlatformAdmin;
  if (t.id === 'error-log') return !!user.isPlatformAdmin;
  return t.roles?.includes(user.role);
});

  const content = (
    <div className="App">
      <header className="App-header">

        {/* ── Logged-out landing ── */}
        {!user && (
          <div className="landing-center">
            <div className="landing-card">

              {/* Logo + powered-by */}
              <div className="landing-logo-wrap">
                <img src="/2-check-logo-colorful.png" className="landing-logo" alt="App logo" />
                <span className="landing-powered">
                  Powered by&nbsp;
                  <img src="/UT_Logo_Black.png" alt="Company logo" />
                </span>
              </div>

              <h1 className="barcode-title">Warehouse Operations</h1>
              <p className="landing-subtitle">Verify orders and receive POs</p>

              {/* Feature pill row */}
              <div style={{
                display: 'flex',
                gap: 8,
                justifyContent: 'center',
                flexWrap: 'wrap',
                margin: '1.25rem 0 0',
              }}>
                {FEATURES.map(f => (
                  <div key={f.label} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '5px 11px',
                    background: 'var(--cyan-light)',
                    border: '1px solid var(--cyan-mid)',
                    borderRadius: 99,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: 'var(--cyan-dark)',
                  }}>
                    <span style={{ display: 'flex', opacity: 0.8 }}>{f.icon}</span>
                    {f.label}
                  </div>
                ))}
              </div>

              <div className="landing-divider" />
              <SignIn onLogin={handleLogin} />
            </div>
          </div>
        )}

        {/* ── Logged-in shell ── */}
        {user && (
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>

            {/* Nav bar */}
<div style={{
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '0 1rem', height: 52, borderBottom: '1px solid var(--border)',
  background: 'var(--surface)', gap: 8,
}}>
  {showSettings && (
  <Modal
    isOpen={showSettings}
    onRequestClose={() => setShowSettings(false)}
    contentLabel="User Settings"
    className="settings-modal"
    overlayClassName="modal-overlay"
  >
    <button className="modal-close-btn" onClick={() => setShowSettings(false)}>×</button>
    <h2 className="modal-title">User settings</h2>
    <div className="modal-field"><span className="modal-label">Name</span>{user.name}</div>
    <div className="modal-field"><span className="modal-label">Role</span>{user.role}</div>

    {user.role === 'Admin' && (
      <AdminPanel
        currentUserEmail={user.email}
        onClose={() => setShowSettings(false)}
        onLogout={handleLogout}
      />
    )}

    {user.role !== 'Admin' && (
      <button className="modal-logout-btn" onClick={handleLogout}>Log out</button>
    )}
  </Modal>
)}
              {/* Left: logo + tabs */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <img
                  src="/2-check-logo-colorful.png"
                  alt="logo"
                  style={{ height: 28, width: 'auto', borderRadius: 6, flexShrink: 0 }}
                />
                <div style={{
                  width: 1,
                  height: 20,
                  background: 'var(--border-dark)',
                  flexShrink: 0,
                }} />
                <div className="nav-tabs-group">
                  {visibleTabs.map(tab => (
                    <button
                      key={tab.id}
                      className={`nav-tab-btn${currentPage === tab.id ? ' active' : ''}`}
                      onClick={() => setCurrentPage(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Right: role badge + name + avatar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                {/* Role badge */}
                <span style={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  padding: '3px 8px',
                  borderRadius: 99,
                  background: user.role === 'Admin'      ? '#fef3c7' :
                              user.role === 'Supervisor' ? '#e0f2fe' :
                              user.role === 'Verifier'   ? '#dcfce7' : 'var(--bg)',
                  color:      user.role === 'Admin'      ? '#92400e' :
                              user.role === 'Supervisor' ? '#075985' :
                              user.role === 'Verifier'   ? '#166534' : 'var(--text-muted)',
                  border: '1px solid',
                  borderColor: user.role === 'Admin'      ? '#fde68a' :
                               user.role === 'Supervisor' ? '#bae6fd' :
                               user.role === 'Verifier'   ? '#bbf7d0' : 'var(--border)',
                }}>
                  {user.role}
                </span>

                <span style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user.name}
                </span>

                <button
                  className="nav-avatar-btn"
                  title="Settings"
                  onClick={() => setShowSettings(true)}
                >
                  {user.name?.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
                </button>
              </div>
            </div>

            {/* Page content */}
            <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%', padding: '0 1.5rem' }}>
              {currentPage === 'home' && (
                <FormFunction
                  user={user}
                  onLogout={handleLogout}
                  showSettingsModal={showSettings}
                  setShowSettingsModal={setShowSettings}
                  initialOrderID={pendingOrderID}
                  onInitialOrderIDHandled={() => setPendingOrderID(null)}
                />
              )}
              {currentPage === 'receiving' && (
                <ReceivingPage user={user} msalInstance={msalInstance} />
              )}
              {currentPage === 'stats' && (
                <StatsPage user={user} msalInstance={msalInstance} />
              )}
              {currentPage === 'unfinished-orders' && (
                <UnfinishedOrdersPage user={user} msalInstance={msalInstance} onOpenOrder={openSavedOrder} />
              )}
              {currentPage === 'error-log' && (
                <ErrorLogPage user={user} msalInstance={msalInstance} />
              )}
              {currentPage === 'platform-admin' && (
                <PlatformAdminPage msalInstance={msalInstance} />
              )}
            </div>
          </div>
        )}

      </header>
    </div>
  );

  return (
    <MsalProvider instance={msalInstance ?? getDummyMsal()}>
      {content}
      <UpdateBanner />
    </MsalProvider>
  );
}